import type { Restaurant } from '@/types';
import { initFirebase, getCurrentUser } from './firebase';
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';
import { collection, doc, getDocs, onSnapshot, query, setDoc, deleteDoc, getFirestore } from 'firebase/firestore';

export const STORAGE_KEY = 'ccq:restaurants';

let initialized = false;
let unsubscribeSnapshot: (() => void) | null = null;

// --- Pending write queue to avoid missed writes before auth is ready ---
 type PendingOp =
  | { kind: 'add'; item: Restaurant }
  | { kind: 'update'; id: string; patch: Partial<Restaurant> }
  | { kind: 'remove'; id: string };
const pendingOps: PendingOp[] = [];
function enqueue(op: PendingOp) { pendingOps.push(op); }
async function flushPending() {
  if (pendingOps.length === 0) return;
  const ctx = await initFirebase();
  const u = getCurrentUser();
  if (!ctx || !u) return; // still not ready
  const householdId = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
  const db = getFirestore();
  const col = householdId && householdId.length > 0
    ? collection(db, 'households', householdId, 'restaurants')
    : collection(db, 'users', u.uid, 'restaurants');
  while (pendingOps.length > 0) {
    const op = pendingOps.shift()!;
    try {
      if (op.kind === 'add') {
        await setDoc(doc(col, op.item.id), op.item, { merge: true });
      } else if (op.kind === 'update') {
        await setDoc(doc(col, op.id), op.patch as Partial<Restaurant>, { merge: true });
      } else if (op.kind === 'remove') {
        await deleteDoc(doc(col, op.id));
      }
    } catch {
      // Put back to queue head and abort to retry later
      pendingOps.unshift(op);
      break;
    }
  }
}

function readLocal(): Restaurant[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeLocal(data: Restaurant[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  // 既存のUIは 'storage' イベントで更新しているが、同一タブ内では発火しない。
  // 同期直後にも更新を反映させるため、疑似的に通知を投げる。
  try {
    // 一部ブラウザでは StorageEvent の new が制限されるため二段構え
    const ev: any = typeof StorageEvent !== 'undefined'
      ? new StorageEvent('storage', { key: STORAGE_KEY })
      : new Event('storage');
    (ev as any).key = (ev as any).key || STORAGE_KEY;
    window.dispatchEvent(ev);
  } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent('ccq:restaurants-changed')); } catch { /* noop */ }
}

export async function startCloudSync() {
  try {
    if (typeof window !== "undefined" && window.location.search.includes("nosync")) return;
  } catch (_e) { /* ignore: non-browser env */ }
  if (initialized) return;
  const ctx = await initFirebase();
  if (!ctx) return;
  initialized = true;

  const auth = getAuth();
  onAuthStateChanged(auth, async (u) => {
    if (unsubscribeSnapshot) {
      try { unsubscribeSnapshot(); } catch (_e) { /* ignore */ }
      unsubscribeSnapshot = null;
    }
    if (!u) return;
    await setupSyncForUser(u);
    // After sync is ready, flush any pending writes queued before auth
    try { await flushPending(); } catch { /* noop */ }
  });
}

async function setupSyncForUser(u: User) {
  const db = getFirestore();
  const householdId = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
  const col = householdId && householdId.length > 0
    ? collection(db, 'households', householdId, 'restaurants')
    : collection(db, 'users', u.uid, 'restaurants');

  if (householdId && householdId.length > 0) {
    try {
      await setDoc(doc(db, 'households', householdId, 'members', u.uid), {
        uid: u.uid,
        joinedAt: new Date().toISOString(),
        email: u.email || null,
      }, { merge: true });
    } catch (_e) { /* ignore: best-effort membership write */ }
  }

  try {
    const snap = await getDocs(query(col));
    const remote: Restaurant[] = [];
    snap.forEach((d) => remote.push(d.data() as Restaurant));
    const local = readLocal();

    const inHouseholdMode = !!(householdId && householdId.length > 0);

    if (inHouseholdMode) {
      // 世帯共有モード: リモートを基準にしつつ、ローカルにのみ存在 or ローカルが新しいものはマージしてアップロード。
      if (remote.length === 0 && local.length > 0) {
        await Promise.all(local.map((it) => setDoc(doc(col, it.id), it, { merge: true })));
        writeLocal(local);
        return;
      }
      if (remote.length > 0 && local.length === 0) {
        writeLocal(remote);
        return;
      }
      if (remote.length > 0 && local.length > 0) {
        const byId = new Map<string, Restaurant>();
        for (const r of remote) byId.set(r.id, r);
        const merged: Restaurant[] = [];
        const seen = new Set<string>();
        // まずローカルを取り込み（新規 or 新しい方を採用）
        for (const l of local) {
          const r = byId.get(l.id);
          if (!r) {
            merged.push(l);
            await setDoc(doc(col, l.id), l, { merge: true });
          } else {
            seen.add(l.id);
            const lu = new Date(l.updatedAt || l.createdAt || 0).getTime();
            const ru = new Date(r.updatedAt || r.createdAt || 0).getTime();
            const keep = lu >= ru ? l : r;
            merged.push(keep);
            if (keep === l) await setDoc(doc(col, l.id), l, { merge: true });
          }
        }
        // リモートにのみあるものを残す
        for (const r of remote) if (!seen.has(r.id)) merged.push(r);
        writeLocal(merged);
        return;
      }
      // 両方空なら何もしない
      return;
    }

    // 従来のユーザ別スコープ: 双方向マージ（互換維持）
    if (local.length === 0 && remote.length > 0) {
      writeLocal(remote);
    } else if (local.length > 0 && remote.length === 0) {
      await Promise.all(local.map((it) => setDoc(doc(col, it.id), it, { merge: true })));
    } else if (local.length > 0 && remote.length > 0) {
      const byId = new Map<string, Restaurant>();
      for (const r of remote) byId.set(r.id, r);
      const merged: Restaurant[] = [];
      const seen = new Set<string>();
      for (const l of local) {
        const r = byId.get(l.id);
        if (!r) {
          merged.push(l);
          await setDoc(doc(col, l.id), l, { merge: true });
        } else {
          seen.add(l.id);
          const lu = new Date(l.updatedAt || l.createdAt || 0).getTime();
          const ru = new Date(r.updatedAt || r.createdAt || 0).getTime();
          const keep = lu >= ru ? l : r;
          merged.push(keep);
          if (keep === l) await setDoc(doc(col, l.id), l, { merge: true });
        }
      }
      for (const r of remote) if (!seen.has(r.id)) merged.push(r);
      writeLocal(merged);
    }
  } catch (_e) { /* ignore: best-effort initial sync */ }

  unsubscribeSnapshot = onSnapshot(col, (snap) => {
    const remote: Restaurant[] = [];
    snap.forEach((d) => remote.push(d.data() as Restaurant));
    writeLocal(remote);
  });
}

// 手動同期: 現在のログインユーザで初期同期と購読をやり直す
export async function syncNow(): Promise<boolean> {
  const ctx = await initFirebase();
  if (!ctx) return false;
  const auth = getAuth();
  const u = auth.currentUser;
  if (!u) return false;
  if (unsubscribeSnapshot) {
    try { unsubscribeSnapshot(); } catch { /* noop */ }
    unsubscribeSnapshot = null;
  }
  await setupSyncForUser(u);
  return true;
}

// 世帯共有モードのときは、書き込み前に必ず members/{uid} を作っておく
async function ensureHouseholdMembership(u: User) {
  try {
    const db = getFirestore();
    const householdId = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
    if (!householdId || householdId.length === 0) return;
    await setDoc(doc(db, 'households', householdId, 'members', u.uid), {
      uid: u.uid,
      joinedAt: new Date().toISOString(),
      email: u.email || null,
    }, { merge: true });
  } catch (_e) {
    // ベストエフォート。失敗しても後段で onSnapshot/初期同期が拾う
  }
}

export async function cloudWriteAdd(item: Restaurant) {
  const ctx = await initFirebase();
  const u = getCurrentUser();
  if (!ctx || !u) { enqueue({ kind: 'add', item }); return; }
  const householdId = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
  if (householdId && householdId.length > 0) {
    await ensureHouseholdMembership(u);
  }
  const col = householdId && householdId.length > 0
    ? collection(ctx.db, 'households', householdId, 'restaurants')
    : collection(ctx.db, 'users', u.uid, 'restaurants');
  await setDoc(doc(col, item.id), item, { merge: true });
}

export async function cloudWriteUpdate(id: string, patch: Partial<Restaurant>) {
  const ctx = await initFirebase();
  const u = getCurrentUser();
  if (!ctx || !u) { enqueue({ kind: 'update', id, patch }); return; }
  const householdId = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
  if (householdId && householdId.length > 0) {
    await ensureHouseholdMembership(u);
  }
  const col = householdId && householdId.length > 0
    ? collection(ctx.db, 'households', householdId, 'restaurants')
    : collection(ctx.db, 'users', u.uid, 'restaurants');
  await setDoc(doc(col, id), patch as Partial<Restaurant>, { merge: true });
}

export async function cloudWriteRemove(id: string) {
  const ctx = await initFirebase();
  const u = getCurrentUser();
  if (!ctx || !u) { enqueue({ kind: 'remove', id }); return; }
  const householdId = (import.meta.env.VITE_CCQ_HOUSEHOLD_ID as string | undefined)?.trim();
  if (householdId && householdId.length > 0) {
    await ensureHouseholdMembership(u);
  }
  const col = householdId && householdId.length > 0
    ? collection(ctx.db, 'households', householdId, 'restaurants')
    : collection(ctx.db, 'users', u.uid, 'restaurants');
  await deleteDoc(doc(col, id));
}
